import PropTypes from "prop-types";
import { Box, Chip, Typography } from "@mui/material";
import { dateLong, dateTime } from "./format";

// Pay later on a case (builder P, API docs/pay-later.md): held 7 days from
// when the customer chose it, a reminder by email on day 2 and day 5, then
// back to a draft if still unpaid. Says until when, which reminders went (or
// when the next one is due), and when it went back to a draft.
const DAY = 24 * 60 * 60 * 1000;
const REMINDER_DAYS = { 1: 2, 2: 5 };
const dayOf = (start, days) => new Date(new Date(start).getTime() + days * DAY).toISOString();

/** The lines to show, from the API's pay_later_history: { title, lines } or null. */
export function payLaterLines(history, { waiting = false } = {}) {
  if (!history?.chosen_at) return null;
  if (history.expired_at) {
    return {
      title: `Back to a draft on ${dateLong(history.expired_at)}`,
      tone: "default",
      lines: [`Not paid within 7 days of choosing Pay later (${dateLong(history.chosen_at)}). The customer was emailed; nothing was lost.`],
    };
  }
  if (!waiting) return null;
  const lines = [1, 2].map((n) => {
    const sent = (history.reminders || []).filter((r) => Number(r.n) === n);
    const ok = sent.find((r) => r.sent);
    if (ok) return `Reminder ${n} (day ${REMINDER_DAYS[n]}): sent ${dateTime(ok.at)}`;
    if (sent.length) return `Reminder ${n} (day ${REMINDER_DAYS[n]}): the mail server refused it; tried again on the next run`;
    return `Reminder ${n} (day ${REMINDER_DAYS[n]}): due ${dateLong(dayOf(history.chosen_at, REMINDER_DAYS[n]))}`;
  });
  return {
    title: `Held until ${dateLong(history.until)}`,
    tone: "warning",
    lines: [`Chosen ${dateTime(history.chosen_at)}. Unpaid by then, it goes back to a draft.`, ...lines],
  };
}

export default function PayLaterHistory({ history, waiting }) {
  const said = payLaterLines(history, { waiting });
  if (!said) return null;
  return (
    <Box mt={1} aria-label="Pay later">
      <Typography variant="body2" fontWeight={700}>
        {waiting ? <Chip size="small" variant="outlined" color="warning" label="Waiting for payment (Pay later)" sx={{ mr: 1 }} /> : null}
        {said.title}
      </Typography>
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
        {said.lines.map((line) => (
          <Typography component="li" variant="body2" color="text.secondary" key={line}>
            {line}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

PayLaterHistory.propTypes = { history: PropTypes.object, waiting: PropTypes.bool };
PayLaterHistory.defaultProps = { history: null, waiting: false };
